import { parseRolePermissions } from "../config/teamRolePermissions";
import {
  ADMIN_ACCESS_PERMISSIONS,
  FULL_ACCESS_PERMISSIONS,
  getDefaultPermissionsForUserType,
} from "../config/registrationPermissions";

/** Single localStorage key for the encrypted login API response. */
export const AUTH_SESSION_KEY = "waSession";

const SESSION_SECRET =
  process.env.REACT_APP_SESSION_SECRET || "wa-interface-session-v1";

const LEGACY_SESSION_KEYS = [
  "authUser",
  "token",
  "isLoggedIn",
  "userEmail",
  "userName",
  "userType",
  "userId",
  "displayName",
  "defaultProjectId",
];

/** Fired in this tab whenever the stored token changes (login, logout, expiry). */
export const AUTH_TOKEN_EVENT = "wa:auth-token";

let sessionCache = undefined;

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === AUTH_SESSION_KEY || event.key == null) {
      sessionCache = undefined;
    }
  });
}

const notifyTokenChange = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_TOKEN_EVENT));
};

const textToBytes = (value) => new TextEncoder().encode(String(value));

const bytesToBase64 = (bytes) => {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode.apply(null, slice);
  }
  return btoa(binary);
};

const base64ToBytes = (value) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const xorBytes = (data, key) => {
  const out = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i += 1) {
    out[i] = data[i] ^ key[i % key.length];
  }
  return out;
};

const encryptJson = (value) => {
  const payload = textToBytes(JSON.stringify(value));
  const key = textToBytes(SESSION_SECRET);
  return bytesToBase64(xorBytes(payload, key));
};

const decryptJson = (encoded) => {
  const payload = base64ToBytes(encoded);
  const key = textToBytes(SESSION_SECRET);
  const json = new TextDecoder().decode(xorBytes(payload, key));
  return JSON.parse(json);
};

const removeLegacyKeys = () => {
  if (typeof localStorage === "undefined") return;
  LEGACY_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Subscribes to token changes in this tab and in other tabs. Long-lived
 * connections use this to reconnect with the current credential.
 */
export const subscribeToToken = (listener) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AUTH_TOKEN_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(AUTH_TOKEN_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
};

/** Decrypt and return the full login API response object. */
export const getLoginSession = () => {
  if (sessionCache !== undefined) return sessionCache;
  if (typeof localStorage === "undefined") {
    sessionCache = null;
    return null;
  }
  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY);
    sessionCache = raw ? decryptJson(raw) : null;
    return sessionCache;
  } catch {
    sessionCache = null;
    return null;
  }
};

/** Store the complete login API success response as one encrypted value. */
export const persistLoginSession = (response) => {
  if (!response || typeof response !== "object") return;
  sessionCache = response;
  localStorage.setItem(AUTH_SESSION_KEY, encryptJson(response));
  removeLegacyKeys();
  notifyTokenChange();
};

/** Parse token from login API response (handles common response shapes). */
export const extractTokenFromLoginResponse = (res) => {
  if (!res || typeof res !== "object") return null;
  return (
    res.token ||
    res.accessToken ||
    res.access_token ||
    res.data?.token ||
    res.data?.accessToken ||
    res.data?.access_token ||
    null
  );
};

export const getToken = () => {
  try {
    return extractTokenFromLoginResponse(getLoginSession() || {}) || null;
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  if (!token) return;
  const session = getLoginSession() || {};
  persistLoginSession({ ...session, token: String(token) });
};

export const getStoredUser = () => {
  const session = getLoginSession();
  if (!session) return null;
  return session.user || session.data?.user || session;
};

export const setStoredUser = (user) => {
  const session = getLoginSession() || {};
  if (!user) {
    persistLoginSession({ ...session, user: null });
    return;
  }
  persistLoginSession({ ...session, user });
};

export const clearSession = () => {
  sessionCache = null;
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_SESSION_KEY);
    removeLegacyKeys();
  }
  notifyTokenChange();
};

export const clearToken = () => {
  clearSession();
};

/** Headers for authenticated apihub requests. */
export const getAuthHeaders = (includeAuth = true) => {
  if (!includeAuth) return {};
  const token = getToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

export const getMatchedUser = (user = getStoredUser()) => {
  const session = getLoginSession();
  return (
    user?.matchedUser ||
    session?.user?.matchedUser ||
    user ||
    session?.user ||
    null
  );
};

const toIdString = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return String(value._id || value.id || value.project_id || "").trim();
  }
  return String(value).trim();
};

export const getUserEmail = (user = getStoredUser()) =>
  user?.email || getMatchedUser(user)?.email || "";

export const getUserId = (user = getStoredUser()) =>
  toIdString(getMatchedUser(user)?._id || user?._id);

export const getUserName = (user = getStoredUser()) => {
  const matched = getMatchedUser(user);
  return (
    matched?.user_name ||
    matched?.display_name ||
    user?.displayName ||
    getUserEmail(user).split("@")[0] ||
    ""
  );
};

export const getDisplayName = (user = getStoredUser()) =>
  getMatchedUser(user)?.display_name || user?.displayName || getUserName(user);

export const getUserProjectIds = (user = getStoredUser()) => {
  const session = getLoginSession();
  const matched = getMatchedUser(user);
  const lists = [
    matched?.project_ids,
    user?.project_ids,
    session?.user?.matchedUser?.project_ids,
    session?.user?.project_ids,
  ];
  const ids = lists.flatMap((list) => (Array.isArray(list) ? list : []));
  return [...new Set(ids.map(toIdString).filter(Boolean))];
};

export const getDefaultProjectId = (user = getStoredUser()) =>
  getUserProjectIds(user)[0] || "";

export const isLoggedIn = () => Boolean(getToken());

/** Normalize DB/login variants: super_admin, super-admin → superadmin */
export const normalizeUserType = (raw) => {
  const type = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  if (type === "super_admin" || type === "superadmin") return "superadmin";
  return type;
};

export const getUserType = (user = getStoredUser()) =>
  normalizeUserType(user?.type || getMatchedUser(user)?.type || "");

export const isAdminUser = (user = getStoredUser()) => {
  const type = getUserType(user);
  return type === "admin" || type === "superadmin";
};

export const isSuperAdminUser = (user = getStoredUser()) =>
  getUserType(user) === "superadmin";

export const isAgentUser = (user = getStoredUser()) => getUserType(user) === "agent";

export const isBusinessOwner = (user = getStoredUser()) => {
  const type = getUserType(user);
  return type === "user" || type === "business" || type === "owner";
};

/**
 * Superadmin may enter any project.
 * Admin may enter only mapped user_projects / project_agents.
 * Agents need a mapping; other owners may proceed when no mapping exists yet.
 */
export const hasProjectAccess = (projectId, user = getStoredUser()) => {
  if (!projectId) return false;
  if (isSuperAdminUser(user)) return true;

  const ids = getUserProjectIds(user);
  const type = getUserType(user);

  if (type === "admin") {
    return ids.includes(String(projectId));
  }

  if (ids.length === 0) return !isAgentUser(user);
  return ids.includes(String(projectId));
};

const permissionFlagOn = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const readTeamRoleLists = (node) => {
  if (!node || typeof node !== "object") return [];
  return [node.teamroles, node.teamRoles, node.team_roles].flatMap((list) =>
    Array.isArray(list) ? list : [],
  );
};

/** Team roles from the encrypted waSession login payload. */
export const getSessionTeamRoles = (user = getStoredUser()) => {
  const session = getLoginSession();
  const matched = getMatchedUser(user);
  const seen = new Set();
  const roles = [];

  const pushAll = (list) => {
    list.forEach((role) => {
      if (!role || typeof role !== "object") return;
      const id = String(role._id || role.id || role.name || JSON.stringify(role));
      if (seen.has(id)) return;
      seen.add(id);
      roles.push(role);
    });
  };

  pushAll(readTeamRoleLists(matched));
  pushAll(readTeamRoleLists(user));
  pushAll(readTeamRoleLists(session?.user?.matchedUser));
  pushAll(readTeamRoleLists(session?.user));
  pushAll(readTeamRoleLists(session));
  return roles;
};

const mergePermissionFlags = (acc, parsed) => {
  Object.entries(parsed || {}).forEach(([key, val]) => {
    if (permissionFlagOn(val)) acc[key] = true;
    else if (!(key in acc)) acc[key] = val;
  });
  return acc;
};

/** Permission flags from login `user.matchedUser.teamroles[].permissions`. */
export const getSessionPermissions = (user = getStoredUser(), projectId) => {
  const session = getLoginSession();
  const matched = getMatchedUser(user);
  const roles = getSessionTeamRoles(user);
  const scoped = projectId
    ? roles.filter((role) => {
        const roleProject = role.project_id || role.projectId || "";
        return !roleProject || String(roleProject) === String(projectId);
      })
    : roles;
  const useRoles = scoped.length > 0 ? scoped : roles;

  const fromRoles = useRoles.reduce(
    (acc, role) => mergePermissionFlags(acc, parseRolePermissions(role)),
    {},
  );
  if (Object.keys(fromRoles).length > 0) return fromRoles;

  const direct =
    matched?.permissions ||
    user?.permissions ||
    session?.permissions ||
    session?.user?.permissions;
  if (!direct) return {};
  return parseRolePermissions({ permissions: direct });
};

export const getAgentPermissions = getSessionPermissions;

export const hasPermission = (
  permissionKey,
  user = getStoredUser(),
  projectId,
) => {
  if (!getToken()) return false;
  if (!permissionKey) return true;

  // Superadmin always uses Full Access from registration.js.
  if (isSuperAdminUser(user)) {
    if (permissionFlagOn(FULL_ACCESS_PERMISSIONS[permissionKey])) return true;
    const wanted = String(permissionKey).toLowerCase();
    const match = Object.keys(FULL_ACCESS_PERMISSIONS).find(
      (key) => key.toLowerCase() === wanted,
    );
    return match ? permissionFlagOn(FULL_ACCESS_PERMISSIONS[match]) : false;
  }

  const roles = getSessionTeamRoles(user);
  let perms = getSessionPermissions(user, projectId);
  let keys = Object.keys(perms);
  const type = getUserType(user);

  // Admin falls back to Admin Access when no team-role flags are present.
  if (keys.length === 0) {
    if (roles.length > 0 && type !== "admin") {
      return false;
    }
    const defaults = getDefaultPermissionsForUserType(type);
    if (defaults) {
      perms = defaults;
      keys = Object.keys(perms);
    } else if (roles.length > 0) {
      return false;
    } else {
      return !isAgentUser(user);
    }
  }

  // Cap admin users to Admin Access even if an old "Full Access" role is linked.
  if (type === "admin" && !permissionFlagOn(ADMIN_ACCESS_PERMISSIONS[permissionKey])) {
    const wantedAdmin = String(permissionKey).toLowerCase();
    const adminKey = Object.keys(ADMIN_ACCESS_PERMISSIONS).find(
      (key) => key.toLowerCase() === wantedAdmin,
    );
    if (!adminKey || !permissionFlagOn(ADMIN_ACCESS_PERMISSIONS[adminKey])) {
      return false;
    }
  }

  if (permissionFlagOn(perms[permissionKey])) return true;
  const wanted = String(permissionKey).toLowerCase();
  const match = keys.find((key) => key.toLowerCase() === wanted);
  return match ? permissionFlagOn(perms[match]) : false;
};

/** Admin + superadmin land on the WABA project picker. */
export const canAccessAllProjects = (user = getStoredUser()) =>
  Boolean(getToken()) && isAdminUser(user);

export const getPostLoginPath = (user = getStoredUser()) => {
  const projectId = getUserProjectIds(user)[0];
  if (isAdminUser(user)) return "/all-projects";
  if (projectId) return `/businessprojects/${projectId}/dashboard`;
  return "/unauthorized";
};
